import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTable } from "react-table";
import { USDollar } from "../../utilities/functions/Helpers";

const addOnLineItemShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  quantity: PropTypes.number,
  unitPrice: PropTypes.number,
  amount: PropTypes.number,
});

const pricingBreakdownShape = PropTypes.shape({
  pricingStrategy: PropTypes.string,
  facilityAmount: PropTypes.number,
  facilityRate: PropTypes.number,
  seatAmount: PropTypes.number,
  seatRate: PropTypes.number,
  seatCount: PropTypes.number,
  cleaningAmount: PropTypes.number,
  cleaningRate: PropTypes.number,
  overtimeAmount: PropTypes.number,
  overtimeRate: PropTypes.number,
  overtimeHours: PropTypes.number,
  bookedHours: PropTypes.number,
  hourlyRate: PropTypes.number,
  addOns: PropTypes.arrayOf(addOnLineItemShape),
});

const InvoiceTable = ({ pricingBreakdown }) => {
  const addOns = Array.isArray(pricingBreakdown?.addOns)
    ? pricingBreakdown.addOns
    : [];

  const data = useMemo(
    () => {
      const rows = [];

      if (Number(pricingBreakdown?.facilityAmount ?? 0) > 0) {
        const hasBookedHours = pricingBreakdown?.bookedHours == null ? false : true;
        const isHourlyFacility = hasBookedHours && pricingBreakdown?.hourlyRate != null;
        rows.push({
          col1: String(rows.length + 1),
          col2:
            pricingBreakdown?.pricingStrategy === "HOURLY"
              ? "Facility Booking"
              : "Facility Rental",
          col3: hasBookedHours ? pricingBreakdown.bookedHours : 1,
          col4: USDollar.format(
            isHourlyFacility
              ? pricingBreakdown.hourlyRate
              : pricingBreakdown?.facilityRate ?? pricingBreakdown?.facilityAmount ?? 0,
          ),
          col5: "Dollar ($)",
          col6: USDollar.format(pricingBreakdown?.facilityAmount ?? 0),
        });
      }

      if (Number(pricingBreakdown?.seatAmount ?? 0) > 0) {
        rows.push({
          col1: String(rows.length + 1),
          col2: "Seat Charges",
          col3: Number(pricingBreakdown?.seatCount ?? 0),
          col4: USDollar.format(pricingBreakdown?.seatRate ?? 0),
          col5: "Dollar ($)",
          col6: USDollar.format(pricingBreakdown?.seatAmount ?? 0),
        });
      }

      if (Number(pricingBreakdown?.cleaningAmount ?? 0) > 0) {
        rows.push({
          col1: String(rows.length + 1),
          col2: "Cleaning Fees",
          col3: "1",
          col4: USDollar.format(pricingBreakdown?.cleaningRate ?? pricingBreakdown?.cleaningAmount ?? 0),
          col5: "Dollar ($)",
          col6: USDollar.format(pricingBreakdown?.cleaningAmount ?? 0),
        });
      }

      if (Number(pricingBreakdown?.overtimeAmount ?? 0) > 0) {
        rows.push({
          col1: String(rows.length + 1),
          col2: "Overtime",
          col3: Number(pricingBreakdown?.overtimeHours ?? 0),
          col4: USDollar.format(pricingBreakdown?.overtimeRate ?? 0),
          col5: "Dollar/Hour ($/h)",
          col6: USDollar.format(pricingBreakdown?.overtimeAmount ?? 0),
        });
      }

      addOns.forEach((requestedAddOn) => {
        rows.push({
          col1: String(rows.length + 1),
          col2: requestedAddOn?.name ?? "Add-On",
          col3: Number(requestedAddOn?.quantity ?? 0),
          col4: USDollar.format(requestedAddOn?.unitPrice ?? 0),
          col5: "Dollar ($)",
          col6: USDollar.format(requestedAddOn?.amount ?? 0),
        });
      });

      return rows;
    },
    [addOns, pricingBreakdown],
  );

  // Define columns
  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "col1",
      },
      {
        Header: "Description",
        accessor: "col2",
      },
      {
        Header: "Qty",
        accessor: "col3",
      },
      {
        Header: "Unit Price",
        accessor: "col4",
      },
      {
        Header: "Unit",
        accessor: "col5",
      },
      {
        Header: "Amount",
        accessor: "col6",
      },
    ],
    [],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data });

  return (
    <div className="w-full overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
      <table {...getTableProps()} className="w-full min-w-[600px] text-left text-sm">
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
            return (
              <tr
                key={headerGroupKey}
                {...headerGroupProps}
                className="bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-800"
              >
                {headerGroup.headers.map((column, index) => {
                  const { key: columnKey, ...columnProps } = column.getHeaderProps();
                  return (
                    <th
                      key={columnKey}
                      {...columnProps}
                      className={`px-6 py-4 font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wider text-xs ${
                        index >= 2 ? "text-right" : "text-left"
                      }`}
                    >
                      {column.render("Header")}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody
          {...getTableBodyProps()}
          className="divide-y divide-slate-100 dark:divide-slate-800"
        >
          {rows.map((row) => {
            prepareRow(row);
            const { key: rowKey, ...rowProps } = row.getRowProps();
            return (
              <tr
                key={rowKey}
                {...rowProps}
                className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
              >
                {row.cells.map((cell, index) => {
                  const { key: cellKey, ...cellProps } = cell.getCellProps();
                  return (
                    <td
                      key={cellKey}
                      {...cellProps}
                      className={`px-6 py-4 text-slate-600 dark:text-slate-300 ${
                        index === 0 ? "font-medium text-slate-400" : ""
                      } ${index >= 2 ? "text-right tabular-nums" : "text-left"} ${
                        index === 1
                          ? "font-medium text-slate-900 dark:text-white"
                          : ""
                      }`}
                    >
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

InvoiceTable.propTypes = {
  pricingBreakdown: pricingBreakdownShape,
};

export default InvoiceTable;
